<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Confirm replacements"
    :submit="onConfirm"
    @close="$emit('close')"
  >
    <template #title>
      <span class="cg-micro d-block"
        >{{ summary.domainName }} · AI assistant</span
      >
      Confirm consumption replacements
    </template>
    <template #body>
      <p class="text-body-secondary small">
        Confirming replaces the standing estimates with actuals. The domain
        figure and every portfolio built on it move with it.
      </p>
      <table class="table table-sm mb-3 cg-key-table">
        <tbody class="cg-tabular">
          <tr>
            <td class="text-body-secondary">Location-months replaced</td>
            <td>
              {{ summary.count }} · {{ summary.locations.join(", ") }} ·
              {{ periodRange }}
            </td>
          </tr>
          <tr>
            <td class="text-body-secondary">Volume</td>
            <td>{{ fmt(summary.volume) }} MWh</td>
          </tr>
          <tr>
            <td class="text-body-secondary">Basis after</td>
            <td>Actual · reported</td>
          </tr>
          <tr>
            <td class="text-body-secondary">Effect on 2026 consumption</td>
            <td>{{ describeDelta(summary.delta) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="alert alert-secondary small mb-0">
        <template v-if="summary.delta === 0">
          The reported figures match the estimates these months were earmarked
          against, so coverage is unchanged.
        </template>
        <template v-else-if="summary.delta > 0">
          The reported figures come in above the standing estimates, so the
          domain needs {{ fmt(summary.delta) }} MWh more cover than planned.
        </template>
        <template v-else>
          The reported figures come in below the standing estimates, so
          {{ fmt(-summary.delta) }} MWh of planned cover is freed up.
        </template>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { computed, onMounted, useTemplateRef } from "vue";
import { describeDelta, type ReplacementSummary } from "../readingsChat";
import { fmt } from "../fixture/format";

const props = defineProps<{ summary: ReplacementSummary }>();
const emit = defineEmits<{ confirm: []; close: [] }>();
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
onMounted(() => modalRef.value?.toggleModal());

const periodRange = computed(() => {
  const periods = props.summary.periods;
  if (periods.length <= 1) return periods[0] ?? "";
  return `${periods[0]} – ${periods[periods.length - 1]}`;
});

// The parent owns the modal's mounted state, so confirming must also ask it to close.
const onConfirm = () => {
  emit("confirm");
  emit("close");
};
</script>
